import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { LOADING_LABELS } from '@kexiaoman/shared';
import { adminApi, ApiError, type Brand, type RevokeUserSessionResult, type SalesChannel, type SeatCreditLedgerRow, type UsageEvent, type UserSeat } from '@/lib/api';
import { ChannelPill } from '@/lib/channel-icons';
import { SeatActionsCell, SeatStatusBadge, effectiveSeatStatus, expiryMeta, seatRemaining } from '@/components/seat-actions';

export function UsersPage() {
  const [query, setQuery] = useState('');
  const [submitted, setSubmitted] = useState<string | null>(null);

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold tracking-tight">用户</h2>

      <Card>
        <CardContent className="pt-6">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (query.trim()) setSubmitted(query.trim());
            }}
            className="flex gap-2"
          >
            <Input
              placeholder="输入手机号或 UUID"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="max-w-md"
            />
            <Button type="submit" disabled={!query.trim()}>
              <Search className="h-4 w-4 mr-1" />
              查询
            </Button>
          </form>
        </CardContent>
      </Card>

      <BatchRevokeSessionsCard />

      {submitted && <UserDetailCard key={submitted} phoneOrUuid={submitted} />}
    </div>
  );
}

function BatchRevokeSessionsCard() {
  const queryClient = useQueryClient();
  const [phonesText, setPhonesText] = useState('');
  const [results, setResults] = useState<RevokeUserSessionResult[]>([]);
  const phones = parsePhoneList(phonesText);
  const revoke = useMutation({
    mutationFn: () => adminApi.revokeUserSessions({ phones }),
    onSuccess: (data) => {
      setResults(data.results);
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.invalidateQueries({ queryKey: ['seats'] });
      toast.success(`已踢出 ${data.summary.revoked} 个用户`);
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : String(error));
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">批量踢出登录</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <Field label="手机号">
          <Textarea
            value={phonesText}
            onChange={(e) => setPhonesText(e.target.value)}
            placeholder="例如：13800138000,13900139000"
            rows={3}
          />
        </Field>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="destructive"
            disabled={!phones.length || revoke.isPending}
            onClick={() => revoke.mutate()}
          >
            {revoke.isPending ? LOADING_LABELS.loading : `踢出 ${phones.length} 个用户`}
          </Button>
          <p className="text-xs text-muted-foreground">
            会禁用对应 Supabase 登录会话，并让后端立即拒绝这些用户的旧 token。
          </p>
        </div>
        {results.length > 0 && <RevokeSessionsResultTable results={results} />}
      </CardContent>
    </Card>
  );
}

function RevokeSessionsResultTable({ results }: { results: RevokeUserSessionResult[] }) {
  return (
    <Table>
      <THead>
        <TR>
          <TH>手机号</TH>
          <TH>用户</TH>
          <TH>状态</TH>
          <TH>说明</TH>
        </TR>
      </THead>
      <TBody>
        {results.map((row) => (
          <TR key={`${row.input}-${row.user_id ?? row.status}`}>
            <TD>{row.phone ?? row.input}</TD>
            <TD className="font-mono text-xs">{row.user_id ? `${row.user_id.slice(0, 8)}…` : '—'}</TD>
            <TD>{revokeStatusLabel(row.status)}</TD>
            <TD className="text-xs text-muted-foreground">{row.message}</TD>
          </TR>
        ))}
      </TBody>
    </Table>
  );
}

function UserDetailCard({ phoneOrUuid }: { phoneOrUuid: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['user', phoneOrUuid],
    queryFn: () => adminApi.getUser(phoneOrUuid),
  });

  if (isLoading) return <p className="text-muted-foreground">{LOADING_LABELS.loading}</p>;
  if (error instanceof ApiError) {
    if (error.status === 404) return <p className="text-sm text-muted-foreground italic">未找到此用户。</p>;
    return <p className="text-sm text-destructive">{error.message}</p>;
  }
  if (!data) return null;

  const summary = data.center.summary;
  const usedRatio = summary.credits_total > 0
    ? Math.min(100, Math.round((summary.credits_used / summary.credits_total) * 100))
    : 0;

  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <div className="space-y-4 lg:col-span-2">
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle>{data.user.phone ? `手机号：${data.user.phone}` : '手机号未设置'}</CardTitle>
              <BrandPill brand={data.brand} />
            </div>
            <p className="text-xs text-muted-foreground font-mono">{data.user.id}</p>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm md:grid-cols-4">
            <Metric label="席位总数" value={summary.total_seats.toString()} />
            <Metric label="已绑定 IP" value={summary.bound_seats.toString()} />
            <Metric label="可新建 IP" value={summary.available_seats.toString()} />
            <Metric label="算力使用" value={`${usedRatio}%`} sub={`${fmtN(summary.credits_used)} / ${fmtN(summary.credits_total)}`} />
          </CardContent>
        </Card>

        <UserBrandCard phoneOrUuid={phoneOrUuid} currentBrand={data.brand} />
        <UserChannelCard
          phoneOrUuid={phoneOrUuid}
          currentChannel={data.center.channel}
        />
        <SeatsCard seats={data.center.seats} phoneOrUuid={phoneOrUuid} />
      </div>

      <div className="space-y-4">
        <SeatLedgerCard ledger={data.center.ledger} />
        <UserHistoryCard userId={data.user.id} />
      </div>
    </div>
  );
}

function BrandPill({ brand }: { brand: Brand | null }) {
  if (!brand) return <span className="text-xs text-muted-foreground">未分配分公司</span>;
  return (
    <span className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-medium">
      {brand.name}
    </span>
  );
}

function UserBrandCard({
  phoneOrUuid,
  currentBrand,
}: {
  phoneOrUuid: string;
  currentBrand: Brand | null;
}) {
  const [brandCode, setBrandCode] = useState(currentBrand?.code ?? '');
  const [note, setNote] = useState('');
  const { data: brands } = useQuery({
    queryKey: ['brands'],
    queryFn: () => adminApi.listBrands({ include_inactive: true }),
  });
  const assign = useMutation({
    mutationFn: () => adminApi.assignUserBrand(phoneOrUuid, {
      brand_code: brandCode,
      note: note.trim(),
    }),
    onSuccess: () => {
      setNote('');
      toast.success('分公司已更新');
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">分公司归属</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">当前分公司</span>
          <BrandPill brand={currentBrand} />
        </div>
        <div className="grid gap-2 md:grid-cols-[180px_1fr_auto] md:items-end">
          <Field label="分公司">
            <Select value={brandCode || undefined} onValueChange={setBrandCode}>
              <SelectTrigger>
                <SelectValue placeholder="请选择" />
              </SelectTrigger>
              <SelectContent>
                {brands?.map((brand) => (
                  <SelectItem key={brand.code} value={brand.code}>
                    {brand.name}{brand.is_active ? '' : '（停用）'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="备注">
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="调整原因" />
          </Field>
          <Button disabled={!brandCode || assign.isPending} onClick={() => assign.mutate()}>
            保存分公司
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function UserChannelCard({
  phoneOrUuid,
  currentChannel,
}: {
  phoneOrUuid: string;
  currentChannel: SalesChannel | null;
}) {
  const [channelId, setChannelId] = useState(currentChannel?.id ?? '');
  const [note, setNote] = useState('');
  const { data: channels } = useQuery({
    queryKey: ['channels'],
    queryFn: () => adminApi.listChannels({ include_inactive: true }),
  });
  const assign = useMutation({
    mutationFn: () => adminApi.assignUserChannel(phoneOrUuid, {
      channel_id: channelId || null,
      note: note.trim(),
    }),
    onSuccess: () => {
      setNote('');
      toast.success('渠道已更新');
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">渠道归属</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">当前渠道</span>
          <ChannelPill channel={currentChannel} />
        </div>
        <div className="grid gap-2 md:grid-cols-[180px_1fr_auto] md:items-end">
          <Field label="渠道">
            <Select value={channelId || 'none'} onValueChange={(v) => setChannelId(v === 'none' ? '' : v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">未分渠道</SelectItem>
                {channels?.map((channel) => (
                  <SelectItem key={channel.id} value={channel.id}>
                    {channel.name}{channel.is_active ? '' : '（停用）'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="备注">
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="调整原因" />
          </Field>
          <Button disabled={assign.isPending} onClick={() => assign.mutate()}>
            保存渠道
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function SeatsCard({ seats, phoneOrUuid }: { seats: UserSeat[]; phoneOrUuid: string }) {
  const queryClient = useQueryClient();
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['user', phoneOrUuid] });
    queryClient.invalidateQueries({ queryKey: ['seats'] });
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">席位</CardTitle>
      </CardHeader>
      <CardContent>
        {seats.length === 0 && <p className="text-sm text-muted-foreground">暂无席位</p>}
        {seats.length > 0 && (
          <Table>
            <THead>
              <TR>
                <TH>席位</TH>
                <TH>渠道</TH>
                <TH>绑定 IP</TH>
                <TH>算力（已用 / 总额 · 剩余）</TH>
                <TH>有效期</TH>
                <TH>操作</TH>
              </TR>
            </THead>
            <TBody>
              {seats.map((seat) => (
                <SeatRow key={seat.id} seat={seat} onChanged={invalidate} />
              ))}
            </TBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function SeatRow({ seat, onChanged }: { seat: UserSeat; onChanged: () => void }) {
  const used = Number(seat.credits_used_cached);
  const granted = Number(seat.credits_granted);
  const remaining = seatRemaining(seat);
  const expiry = expiryMeta(seat.valid_until);

  return (
    <TR>
      <TD>
        <div className="font-mono text-[10px]">{seat.id.slice(0, 8)}…</div>
        <div className="mt-0.5 flex items-center gap-1">
          <SeatStatusBadge status={effectiveSeatStatus(seat)} />
          <span className="text-[10px] text-muted-foreground">{seat.source}</span>
        </div>
      </TD>
      <TD><ChannelPill channel={seat.channel} /></TD>
      <TD>{seat.current_ip?.name ?? '未绑定'}</TD>
      <TD className="font-mono text-xs">
        {fmtN(used)} / {fmtN(granted)}
        <div className={`text-[10px] ${remaining <= 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
          剩余 {fmtN(remaining)}
        </div>
      </TD>
      <TD className="text-xs">
        <div>{dateTime(seat.valid_from)}</div>
        <div className={expiry.className}>
          至 {dateTime(seat.valid_until)}
          {expiry.expired ? ' · 已过期' : expiry.soon ? ' · 即将到期' : ''}
        </div>
      </TD>
      <TD>
        <SeatActionsCell seat={seat} onChanged={onChanged} />
      </TD>
    </TR>
  );
}

function UserHistoryCard({ userId }: { userId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['user-history', userId],
    queryFn: () => adminApi.userHistory(userId, 30),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">最近消耗 (30 条)</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading && <p className="text-muted-foreground text-sm">{LOADING_LABELS.loading}</p>}
        {data && data.length === 0 && <p className="text-muted-foreground text-sm italic">无记录</p>}
        {data && data.length > 0 && (
          <div className="space-y-1.5 max-h-[500px] overflow-y-auto text-xs">
            {data.map((event) => (
              <UsageRow key={event.id} event={event} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SeatLedgerCard({ ledger }: { ledger: SeatCreditLedgerRow[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">席位调额日志</CardTitle>
      </CardHeader>
      <CardContent>
        {ledger.length === 0 && <p className="text-muted-foreground text-sm italic">暂无调额记录</p>}
        {ledger.length > 0 && (
          <div className="space-y-1.5 max-h-[300px] overflow-y-auto text-xs">
            {ledger.slice(0, 30).map((row) => (
              <div key={row.id} className="border-b border-border/50 pb-1.5">
                <div className="flex justify-between gap-2">
                  <span className="font-medium">{ledgerLabel(row.op_type)}</span>
                  <span className={Number(row.delta_credits) >= 0 ? 'text-green-700' : 'text-destructive'}>
                    {Number(row.delta_credits) >= 0 ? '+' : ''}{fmtN(Number(row.delta_credits))}
                  </span>
                </div>
                <div className="font-mono text-[10px] text-muted-foreground">
                  seat {row.seat_id.slice(0, 8)} · after {fmtN(Number(row.credits_after))}
                </div>
                <div className="text-muted-foreground">
                  {row.note || '无备注'} · {new Date(row.created_at).toLocaleString('zh-CN')}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function UsageRow({ event }: { event: UsageEvent }) {
  return (
    <div className="flex justify-between gap-2 border-b border-border/50 pb-1.5">
      <div className="truncate">
        <div className="font-mono">{event.model}</div>
        <div className="text-muted-foreground">
          {event.input_tokens} in / {event.output_tokens} out · {event.status}
        </div>
        <div className="font-mono text-[10px] text-muted-foreground">
          seat {event.seat_id ? event.seat_id.slice(0, 8) : '—'} · ip {event.ip_id ? event.ip_id.slice(0, 8) : '—'}
        </div>
      </div>
      <div className="text-right flex-shrink-0">
        <div className="font-medium">{fmtN(event.credits)}</div>
        <div className="text-muted-foreground">{new Date(event.created_at).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</div>
      </div>
    </div>
  );
}

function Metric({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold">{value}</div>
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function fmtN(n: number): string {
  return n.toLocaleString('en-US', { maximumFractionDigits: 4 });
}

function dateTime(iso: string): string {
  return new Date(iso).toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

function ledgerLabel(opType: string): string {
  if (opType === 'default_grant') return '默认席位';
  if (opType === 'application_grant') return '申请审批';
  if (opType === 'admin_grant') return '管理员增加';
  if (opType === 'admin_deduct') return '管理员扣减';
  if (opType === 'admin_set') return '管理员设置';
  return opType;
}

function parsePhoneList(value: string): string[] {
  return Array.from(
    new Set(
      value
        .split(',')
        .flatMap((item) => {
          const phone = item.trim();
          return phone ? [phone] : [];
        }),
    ),
  );
}

function revokeStatusLabel(status: RevokeUserSessionResult['status']): string {
  if (status === 'revoked') return '已踢出';
  if (status === 'invalid_phone') return '手机号无效';
  if (status === 'not_found') return '未找到用户';
  return '失败';
}

export const _UsersTable = Table;
export const _THead = THead;
export const _TBody = TBody;
export const _TR = TR;
export const _TH = TH;
export const _TD = TD;
export const _Label = Label;
